import { useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'

import { useAppAuthenticationManager } from '../components/AppAuthenticationManager'
import { useAppConnectionManager } from '../components/AppConnectionManager'
import { useAppLoginManager } from '../components/AppLoginManager'
import {
  ApplicationAccountUserModel,
  ApplicationAccountUserStatus,
} from '../models/applicationAccountUser'
import { ApplicationGetUserInfo, useGetUserInfo } from './useGetUserInfo'

type AppConfig = {
  name: string
  badge?: string
  badgeColor?: 'success' | 'warning'
  onItemClick: () => void
}

export function useAppConfig() {
  const { t } = useTranslation('common')
  const { handleConnectApplication } = useAppConnectionManager()
  const { handleAuthenticateApplication } = useAppAuthenticationManager()
  const { handleLoginApplication } = useAppLoginManager()
  const { userInfo } = useGetUserInfo()

  const USER_ALLOWED_APPLICATIONS: ApplicationGetUserInfo[] = useMemo(() => {
    if (!userInfo) {
      Sentry.captureMessage(
        'User info is required to create app config',
        'fatal',
      )

      return []
    }

    return (
      userInfo?.applications?.filter((app) =>
        userInfo?.applications?.find(
          (allowedApp) => allowedApp.applicationKey === app.applicationKey,
        ),
      ) || []
    )
  }, [userInfo])

  const USER_APPLICATIONS_ACCOUNT_USERS = useMemo(() => {
    if (!userInfo) {
      Sentry.captureMessage(
        'User info is required to create app config',
        'fatal',
      )
      return []
    }

    return USER_ALLOWED_APPLICATIONS.reduce(
      (acc, application) => {
        const accountUser = userInfo.ApplicationAccountUsers.find(
          (accountUser) => accountUser.applicationId === application.id,
        )

        if (accountUser) {
          acc.push({ application, accountUser })
        }

        return acc
      },
      [] as {
        application: ApplicationGetUserInfo
        accountUser: ApplicationAccountUserModel
      }[],
    )
  }, [USER_ALLOWED_APPLICATIONS, userInfo])

  const APPS = useMemo(() => {
    if (!userInfo) {
      Sentry.captureMessage(
        'User info is required to create app config',
        'fatal',
      )
      return []
    }

    const applicationToStatus: Record<
      ApplicationAccountUserStatus,
      (
        application: ApplicationGetUserInfo,
        accountUser: ApplicationAccountUserModel,
      ) => AppConfig
    > = {
      [ApplicationAccountUserStatus.CONNECT]: (application) => ({
        name: application.title,
        badge: t('sidebar.app_selection.badges.connect'),
        badgeColor: 'success' as const,
        onItemClick: () =>
          handleConnectApplication(
            application.id,
            userInfo.id,
            application.title,
          ),
      }),
      [ApplicationAccountUserStatus.AUTHENTICATE]: (
        application,
        accountUser,
      ) => ({
        name: application.title,
        badge: t('sidebar.app_selection.badges.authenticate'),
        badgeColor: 'warning' as const,
        onItemClick: () =>
          handleAuthenticateApplication(
            application.id,
            userInfo.id,
            application.title,
            accountUser.id,
          ),
      }),
      [ApplicationAccountUserStatus.CONNECTED]: (application, accountUser) => ({
        name: application.title,
        onItemClick: () =>
          handleLoginApplication(
            application.id,
            userInfo.id,
            application.title,
            accountUser.id,
          ),
      }),
      [ApplicationAccountUserStatus.REMOVED]: (application) => ({
        name: application.title,
        onItemClick: () => {},
      }),
    }

    return USER_APPLICATIONS_ACCOUNT_USERS.map(({ application, accountUser }) =>
      applicationToStatus[accountUser.status](application, accountUser),
    )
  }, [
    USER_APPLICATIONS_ACCOUNT_USERS,
    userInfo,
    handleConnectApplication,
    handleAuthenticateApplication,
    handleLoginApplication,
    t,
  ])

  return { APPS }
}
